import { ArrowLeft, Check, ChevronLeft, ChevronRight, Clock, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '../components/Badge';
import {
  useApprovePendingToolAction,
  usePendingToolActions,
  useRejectPendingToolAction,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

function statusBadgeVariant(status: string): 'warning' | 'success' | 'danger' | 'default' {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
    case 'expired':
      return 'danger';
    default:
      return 'default';
  }
}

function formatInput(input: Record<string, unknown>) {
  const json = JSON.stringify(input);
  return json.length > 60 ? `${json.slice(0, 60)}...` : json;
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function useNowTick(intervalMs: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(interval);
  }, [intervalMs]);
  return now;
}

export function PendingToolActions() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { data, isLoading } = usePendingToolActions({
    page,
    limit: 20,
    search: search || undefined,
  });
  const approve = useApprovePendingToolAction();
  const reject = useRejectPendingToolAction();
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );
  const now = useNowTick(1000);

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0;

  function showNotification(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }

  function handleApprove(id: string) {
    approve.mutate(id, {
      onSuccess: () => showNotification('success', 'Tool action approved'),
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        showNotification(
          'error',
          status === 409
            ? 'This action was already resolved or has expired'
            : 'Failed to approve tool action',
        );
      },
    });
  }

  function handleReject(id: string) {
    reject.mutate(id, {
      onSuccess: () => showNotification('success', 'Tool action rejected'),
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        showNotification(
          'error',
          status === 409
            ? 'This action was already resolved or has expired'
            : 'Failed to reject tool action',
        );
      },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/platform-health')}
          className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">Pending Tool Approvals</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Chat tool calls waiting for human approval
            {data && <span className="ml-1 font-medium text-warning">({data.total} pending)</span>}
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          placeholder="Search by tool name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full bg-surface border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
        />
      </div>

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 font-medium text-text-muted">Tool</th>
              <th className="px-4 py-3 font-medium text-text-muted">Status</th>
              <th className="px-4 py-3 font-medium text-text-muted">Organization</th>
              <th className="px-4 py-3 font-medium text-text-muted">Conversation</th>
              <th className="px-4 py-3 font-medium text-text-muted">Input</th>
              <th className="px-4 py-3 font-medium text-text-muted">Expires</th>
              <th className="px-4 py-3 font-medium text-text-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && data?.data.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                  No pending tool actions found.
                </td>
              </tr>
            )}
            {data?.data.map((action) => {
              const remainingMs = new Date(action.expiresAt).getTime() - now;
              const expired = remainingMs <= 0;
              const isPending = action.status === 'pending';
              const isResolving =
                (approve.isPending && approve.variables === action.id) ||
                (reject.isPending && reject.variables === action.id);

              return (
                <tr
                  key={action.id}
                  className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-text">{action.toolName}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusBadgeVariant(expired && isPending ? 'expired' : action.status)}>
                      {expired && isPending ? 'expired' : action.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-text-secondary font-mono text-xs">
                    {action.organizationId}
                  </td>
                  <td className="px-4 py-3 text-text-secondary font-mono text-xs">
                    {action.conversationId}
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-background px-2 py-1 rounded text-text-muted max-w-[200px] truncate block">
                      {formatInput(action.input)}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-xs text-warning">
                      <Clock size={12} />
                      {isPending ? (expired ? 'expired' : formatCountdown(remainingMs)) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {isPending && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(action.id)}
                          disabled={isResolving || expired}
                          aria-label="Approve"
                          className="p-1.5 rounded-md border border-success/40 text-success hover:bg-success/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => handleReject(action.id)}
                          disabled={isResolving || expired}
                          aria-label="Reject"
                          className="p-1.5 rounded-md border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-muted">
            Page {page} of {totalPages} ({data?.total} total)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-md border border-border text-text-muted hover:text-text hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-md border border-border text-text-muted hover:text-text hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {notification && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border text-sm shadow-lg z-50 ${
            notification.type === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
}
